import { useState } from 'react';
import { Dialog } from '../dialog-portal/dialog.tsx';
import { InfoIcon } from '../dialog-portal/infoIcon.tsx';
import { CmmFileImport } from '../../services/cmm-import/types.ts';
import { Accordion } from '../accordion.tsx';
import { ToggleButton } from '../toggleButton.tsx';

export function CmmPositionsAccordion({
    fileIndex,
    importFileResult,
    positionNumberCounts,
    onChange,
}: {
    fileIndex: number;
    importFileResult: CmmFileImport;
    positionNumberCounts: Map<string, number>;
    onChange: (index: number, newImportFileResult: CmmFileImport) => void;
}) {
    return (
        <Accordion
            style={{ marginBottom: '24px', minWidth: '600px' }}
            initialOpen={importFileResult.isIncluded}
            title={<FileHeader fileIndex={fileIndex} importFileResult={importFileResult} onChange={onChange} />}
        >
            <PositionsSelect
                fileIndex={fileIndex}
                importFileResult={importFileResult}
                positionNumberCounts={positionNumberCounts}
                onChange={onChange}
            />
        </Accordion>
    );
}

function FileHeader({
    fileIndex,
    importFileResult,
    onChange,
}: {
    fileIndex: number;
    importFileResult: CmmFileImport;
    onChange: (index: number, newImportFileResult: CmmFileImport) => void;
}) {
    const handleFileSelectChange = (isSelected: boolean) => {
        const newImportFileResult = structuredClone(importFileResult);
        newImportFileResult.isIncluded = isSelected;
        onChange(fileIndex, newImportFileResult);
    };

    return (
        <div style={{ margin: '8px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', width: '100%' }}>
            {importFileResult.filename}
            <ToggleButton
                style={{ marginLeft: '12px' }}
                selection={importFileResult.isIncluded}
                onSelectionChange={handleFileSelectChange}
                disabled={!importFileResult.isImportable}
            />
        </div>
    );
}

function PositionsSelect({
    fileIndex,
    importFileResult,
    positionNumberCounts,
    onChange,
}: {
    fileIndex: number;
    importFileResult: CmmFileImport;
    positionNumberCounts: Map<string, number>;
    onChange: (index: number, newImportFileResult: CmmFileImport) => void;
}) {
    const [isWarningInfoOpen, setIsWarningInfoOpen] = useState(false);

    const handlePositionSelectChange = (positionIndex: number, isSelected: boolean) => {
        if (!importFileResult.isIncluded) return;
        const newImportFileResult = structuredClone(importFileResult);
        newImportFileResult.positions![positionIndex].isIncluded = isSelected;
        onChange(fileIndex, newImportFileResult);
    };

    if (importFileResult.errorMessage)
        return (
            <p className='error' style={{ marginLeft: '20px' }}>
                {importFileResult.errorMessage}
            </p>
        );
    return (
        <>
            {importFileResult.warningMessage && (
                <>
                    <div
                        className='flex--horz'
                        style={{
                            gap: '6px',
                            margin: '8px 0 12px 20px',
                            alignItems: 'center',
                            padding: '6px 10px',
                        }}
                    >
                        <p className='warning' style={{ margin: 0 }}>
                            {importFileResult.warningMessage}
                        </p>
                        <InfoIcon onClick={() => setIsWarningInfoOpen(true)} title='How CMM config matching works' />
                    </div>
                    {isWarningInfoOpen && (
                        <Dialog title='CMM Configuration Matching' onClose={() => setIsWarningInfoOpen(false)}>
                            <div style={{ maxWidth: 620, lineHeight: 1.5 }}>
                                <p>This file matched multiple CMM configurations, so the first match was used.</p>
                                <p>Selection process:</p>
                                <ol>
                                    <li>Only configurations with matching file type are considered.</li>
                                    <li>Metadata validation is run for each configuration.</li>
                                    <li>If exactly one configuration matches metadata, that one is used.</li>
                                    <li>If multiple configurations match metadata, the first one in CMM Configs order is used.</li>
                                </ol>
                                <p>When Equipment name cell matters:</p>
                                <ol>
                                    <li>Use it when multiple machines share identical metadata validation rules.</li>
                                    <li>Set Equipment name cell in each such config to the cell containing machine name in the report.</li>
                                    <li>
                                        If that cell value matches the machine display name in Resources / Custom equipments, import can select the
                                        intended config.
                                    </li>
                                </ol>
                            </div>
                        </Dialog>
                    )}
                </>
            )}
            {importFileResult.hasMultipleParts && <p className='warning'>Warning: The file contains multiple parts</p>}
            <table className='position-import-table'>
                <thead>
                    <tr>
                        <th>Position</th>
                        <th>Value</th>
                        <th>Issues</th>
                        <th>Import</th>
                    </tr>
                </thead>
                <tbody>
                    {importFileResult.positions?.map((it, index) => {
                        const includedCount = positionNumberCounts.get(it.positionNumber) || 0;
                        const issues = includedCount > 1 ? [...it.issues, 'Position selected multiple times'] : it.issues;

                        const isIncluded = importFileResult.isIncluded && it.isIncluded;

                        return (
                            <tr key={index}>
                                <td>{it.positionNumber}</td>
                                <td>{it.value}</td>
                                <td style={{ whiteSpace: 'pre-wrap' }}>{issues.join('\n')}</td>
                                <td>
                                    <ToggleButton
                                        selection={isIncluded}
                                        onSelectionChange={(isSelected) => handlePositionSelectChange(index, isSelected)}
                                        disabled={!it.isImportable}
                                    />
                                </td>
                            </tr>
                        );
                    })}
                </tbody>
            </table>
        </>
    );
}
